import { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  IconButton,
  Stack,
  CircularProgress
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { ServerConfig } from "../../../connectors/Config";
import { AuthHelper } from "../../controllers/AuthHelper";

/* ===============================
   FORMATTAZIONE PREZZO
   =============================== */
const formatPrice = (value) => {
  const n = Number(value);
  if (isNaN(n)) return "0,00";
  return n.toFixed(2).replace(".", ",");
};

const emptyForm = {
  id: null,
  nome: "",
  descrizione: "",
  prezzo: "0,00",
  attivo: true,
  corsi: []
};

const Pacchetti = () => {
  const [subCentro, setSubCentro] = useState(null);
  const [packages, setPackages] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const API = ServerConfig.AWS.Serverless.API_BASE_URL_2;

  /* ===============================
     FETCH pacchetti + corsi disponibili
     =============================== */
  const fetchAll = useCallback(async (sub) => {
    setLoading(true);
    try {
      const [pkgRes, coursesRes] = await Promise.all([
        fetch(`${API}/packages/list/${sub}`),
        fetch(`${API}/prezziario/search/${sub}`)
      ]);

      if (pkgRes.ok) {
        setPackages((await pkgRes.json()) || []);
      } else {
        setPackages([]);
      }

      if (coursesRes.ok) {
        let data = await coursesRes.json();
        // l'endpoint prezziario puo' restituire una stringa JSON
        if (typeof data === "string") {
          try { data = JSON.parse(data); } catch (e) { data = []; }
        }
        setAvailableCourses(Array.isArray(data) ? data : []);
      } else {
        setAvailableCourses([]);
      }
    } catch (err) {
      console.error("Errore caricamento pacchetti:", err);
      alert("Errore nel caricamento dei pacchetti");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    (async () => {
      try {
        const user = await AuthHelper.getUserFromIdToken();
        setSubCentro(user.Username);
        await fetchAll(user.Username);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();
  }, [fetchAll]);

  /* ===============================
     APERTURA FORM
     =============================== */
  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (pkg) => {
    setForm({
      id: pkg.id,
      nome: pkg.nome || "",
      descrizione: pkg.descrizione || "",
      prezzo: formatPrice(pkg.prezzo),
      attivo: pkg.attivo !== false,
      corsi: (pkg.corsi || []).map((c) => c.id_corso)
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  /* ===============================
     HANDLERS FORM
     =============================== */
  const handlePriceChange = (value) => {
    let sanitized = value.replace(/[^0-9,]/g, "");
    const parts = sanitized.split(",");
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setForm((f) => ({ ...f, prezzo: sanitized }));
  };

  const handlePriceBlur = () => {
    if (!form.prezzo || form.prezzo === ",") {
      setForm((f) => ({ ...f, prezzo: "0,00" }));
      return;
    }
    const n = Number(form.prezzo.replace(",", "."));
    if (isNaN(n)) return;
    setForm((f) => ({ ...f, prezzo: n.toFixed(2).replace(".", ",") }));
  };

  /* ===============================
     SALVATAGGIO
     =============================== */
  const handleSave = async () => {
    if (!form.nome || form.nome.trim() === "") {
      alert("Inserisci il nome del pacchetto");
      return;
    }
    if (!form.corsi || form.corsi.length === 0) {
      alert("Seleziona almeno un corso da includere nel pacchetto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: form.id || undefined,
        nome: form.nome.trim(),
        descrizione: form.descrizione || "",
        prezzo: Number(String(form.prezzo).replace(",", ".")) || 0,
        attivo: form.attivo,
        corsi: form.corsi
      };
      const res = await fetch(`${API}/packages/save/${subCentro}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Errore salvataggio");
      setDialogOpen(false);
      await fetchAll(subCentro);
      alert("Pacchetto salvato con successo");
    } catch (err) {
      console.error("Errore salvataggio pacchetto:", err);
      alert("Errore durante il salvataggio del pacchetto");
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
     ELIMINAZIONE
     =============================== */
  const handleDelete = async (pkg) => {
    if (!window.confirm(`Eliminare il pacchetto "${pkg.nome}"?`)) return;
    try {
      const res = await fetch(`${API}/packages/delete/${pkg.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error("Errore eliminazione");
      await fetchAll(subCentro);
    } catch (err) {
      console.error("Errore eliminazione pacchetto:", err);
      alert("Errore durante l'eliminazione del pacchetto");
    }
  };

  const courseName = (id) => {
    const c = availableCourses.find((x) => x.id_corso === id);
    return c ? c["nome corso"] : id;
  };

  return (
    <Container sx={{ maxWidth: "1400px", mt: 6, mb: 6 }}>
      {/* HEADER */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background:
            "linear-gradient(90deg, rgba(4,14,99,1) 0%, rgba(40,80,180,1) 100%)",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Pacchetti corsi
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
            Crea pacchetti di corsi con un prezzo unico. Il marittimo che si
            iscrive al pacchetto viene iscritto a tutti i corsi inclusi.
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            backgroundColor: "#fff",
            color: "rgba(4,14,99,1)",
            fontWeight: "bold",
            px: 3,
            py: 1.2,
            borderRadius: 99,
            "&:hover": { backgroundColor: "#eef1fb" }
          }}
        >
          Nuovo pacchetto
        </Button>
      </Paper>

      {/* LISTA */}
      <Paper elevation={3} sx={{ borderRadius: 4, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f7fb" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Nome pacchetto</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Corsi inclusi</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} width={140}>
                  Prezzo (€)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} width={90}>
                  Stato
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} width={120} align="right">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5, color: "#888" }}>
                    Nessun pacchetto creato. Clicca su “Nuovo pacchetto” per iniziare.
                  </TableCell>
                </TableRow>
              )}
              {packages.map((pkg) => (
                <TableRow key={pkg.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{pkg.nome}</Typography>
                    {pkg.descrizione && (
                      <Typography variant="body2" sx={{ color: "#777" }}>
                        {pkg.descrizione}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(pkg.corsi || []).map((c) => (
                        <Chip
                          key={c.id_corso}
                          label={c.nome_corso || courseName(c.id_corso)}
                          size="small"
                          sx={{ backgroundColor: "#eef1fb", color: "rgba(4,14,99,1)" }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{formatPrice(pkg.prezzo)}</TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.attivo !== false ? "Attivo" : "Non attivo"}
                      size="small"
                      color={pkg.attivo !== false ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(pkg)} title="Modifica">
                      <EditIcon sx={{ color: "rgba(4,14,99,1)" }} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(pkg)} title="Elimina">
                      <DeleteIcon sx={{ color: "#c0392b" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* DIALOG CREAZIONE/MODIFICA */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: "bold", color: "rgba(4,14,99,1)" }}>
          {form.id ? "Modifica pacchetto" : "Nuovo pacchetto"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Nome pacchetto"
              fullWidth
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            />
            <TextField
              label="Descrizione (opzionale)"
              fullWidth
              multiline
              minRows={2}
              value={form.descrizione}
              onChange={(e) =>
                setForm((f) => ({ ...f, descrizione: e.target.value }))
              }
            />
            <FormControl fullWidth>
              <InputLabel id="corsi-label">Corsi inclusi</InputLabel>
              <Select
                labelId="corsi-label"
                multiple
                value={form.corsi}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    corsi:
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                  }))
                }
                input={<OutlinedInput label="Corsi inclusi" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => (
                      <Chip key={id} label={courseName(id)} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableCourses.length === 0 && (
                  <MenuItem disabled>
                    Nessun corso disponibile (verifica il prezziario)
                  </MenuItem>
                )}
                {availableCourses.map((c) => (
                  <MenuItem key={c.id_corso} value={c.id_corso}>
                    <Checkbox checked={form.corsi.indexOf(c.id_corso) > -1} />
                    <ListItemText primary={c["nome corso"]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Prezzo pacchetto (€)"
              fullWidth
              value={form.prezzo}
              onChange={(e) => handlePriceChange(e.target.value)}
              onBlur={handlePriceBlur}
              inputProps={{ inputMode: "decimal", pattern: "[0-9,]*" }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.attivo}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, attivo: e.target.checked }))
                  }
                />
              }
              label="Pacchetto attivo (visibile ai marittimi)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: "rgba(4,14,99,1)",
              color: "#fff",
              fontWeight: "bold",
              px: 4,
              borderRadius: 99,
              "&:hover": { backgroundColor: "rgba(4,14,99,0.9)" }
            }}
          >
            {saving ? "Salvataggio..." : "Salva pacchetto"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Pacchetti;
